import type { UseGridColumnsOptions } from "src/components/Grid/hooks/columnsExtension/useGridColumns";

export const COLUMN_OPTIONS = [
    { id: "title", label: "Actions", defaultVisible: true },
    { id: "categories", label: "Category", defaultVisible: true },
    { id: "startYear", label: "Start year", defaultVisible: true },
    { id: "endYear", label: "End year", defaultVisible: true },
    { id: "status", label: "Status", defaultVisible: true },
    { id: "impact", label: "Impact", defaultVisible: true },
] as const;

export type ActionPlanActionsListColumnId = (typeof COLUMN_OPTIONS)[number]["id"];

export type ActionPlanActionsListColumnConfig = {
    id: ActionPlanActionsListColumnId;
    visible: boolean;
};

export type ActionPlanActionsListConfigProps = {
    columns?: ActionPlanActionsListColumnConfig[];
};

export const normalizeColumnsConfig = (value: unknown): ActionPlanActionsListColumnConfig[] => {
    if (!Array.isArray(value)) {
        return COLUMN_OPTIONS.map((o) => ({ id: o.id, visible: o.defaultVisible }));
    }

    const allowed = new Set<ActionPlanActionsListColumnId>(COLUMN_OPTIONS.map((o) => o.id));
    const isAllowedId = (id: string): id is ActionPlanActionsListColumnId => {
        return allowed.has(id as ActionPlanActionsListColumnId);
    };
    const order: ActionPlanActionsListColumnId[] = [];
    const visibility = new Map<ActionPlanActionsListColumnId, boolean>();

    for (const v of value) {
        if (!v || typeof v !== "object") continue;
        const raw = v as Record<string, unknown>;
        const id = typeof raw.id === "string" ? raw.id : null;
        if (id === null || !isAllowedId(id)) continue;

        const typedId = id;
        if (visibility.has(typedId)) continue;

        order.push(typedId);
        visibility.set(typedId, raw.visible === true);
    }

    for (const opt of COLUMN_OPTIONS) {
        if (visibility.has(opt.id)) continue;
        order.push(opt.id);
        visibility.set(opt.id, opt.defaultVisible);
    }

    return order.map((id) => ({ id, visible: visibility.get(id) === true }));
};

export const buildGridColumnsOptionsFromColumnsConfig = (value: unknown): UseGridColumnsOptions => {
    const columns = normalizeColumnsConfig(value);

    return {
        persist: false,
        initialColumnOrder: columns.map((c) => c.id),
        initialColumnVisibility: Object.fromEntries(columns.map((c) => [c.id, c.visible] as const)),
    };
};
